<template>
  <div class="flex-rows w-full bg-white p-2 rounded-lg my-2 pb-6">
    <div class="flex w-full justify-end items-center">
      <Cross @click="handleCancel" />
    </div>
    <FormulateForm @submit="handleSubmit">
      <div class="flex w-full justify-between items-start gap-5">
        <!-- Category -->
        <div class="w-3/12">
          <FormulateInput
            v-model="form.category"
            v-bind="multiSelectProps"
            :label="$t('components.risk_mitigation.category')"
            :placeholder="$t('components.risk_mitigation.category')"
            validation="required"
            :options="localDropdownOptions.categories"
            emit-values
            @tag="(newTag) => addNewTag(newTag, 'categories', 'category')"
          />
        </div>
        <!-- Sub-Category -->
        <div class="w-3/12">
          <FormulateInput
            v-model="form.sub_category"
            v-bind="multiSelectProps"
            :label="$t('components.risk_mitigation.subcategory')"
            :placeholder="$t('components.risk_mitigation.subcategory')"
            validation="required"
            :options="localDropdownOptions.subcategories"
            emit-values
            @tag="(newTag) => addNewTag(newTag, 'subcategories', 'sub_category')"
          />
        </div>
        <!-- Title -->
        <div class="w-3/12">
          <FormulateInput
            v-model="form.title"
            v-bind="multiSelectProps"
            :label="$t('components.risk_mitigation.title')"
            :placeholder="$t('components.risk_mitigation.title')"
            validation="required"
            :options="localDropdownOptions.titles"
            emit-values
            @tag="(newTag) => addNewTag(newTag, 'titles', 'title')"
          />
        </div>
        <!-- Description -->
        <div class="w-2/12">
          <FormulateInput
            type="text"
            v-model="form.description"
            :label="$t('components.risk_mitigation.description')"
            :placeholder="$t('components.risk_mitigation.description')"
            validation="max:200,length"
            :validation-messages="{
              max: $t('components.risk_mitigation.field_max_error', { max: 200 })
            }"
            :element-class="
              (context, classes) => ['flex-1 min-w-full'].concat(classes)
            "
            error-behavior="value"
            @validation="validationDescription"
          />
        </div>
        <!-- Submit -->
        <div class="flex mt-7 w-1/12 items-center">
          <FormulateInput
            class="px-3"
            type="submit"
            :text="$t('actions.submit')"
            :disabled="getDisableState"
          >
            <span class="px-2">
              {{
                mode !== 'edit' ? 
                  $t('actions.add') :
                  $t('actions.update')
              }}
            </span>
            <span class="px-1" v-if="createRiskMitigationLoading">
              <Loader size="3" />
            </span>
          </FormulateInput>
        </div>
      </div>
    </FormulateForm>
  </div>
</template>

<script>
import { createRiskMitigation, updateRiskMitigation} from "../service";
import Cross from "@shared/components/icon-buttons/Cross.vue";
import Loader from "@shared/loader";
export default {
  name: "createRiskMitigation",
  components: {
    Cross,
    Loader,
  },
  props: {
    mode: {
      type: String,
      default: "create",
    },
    formData: {
      type: Object,
      default: () => {},
    },
    riskAssessmentId: {
      type: String,
      default: ''
    },
    dropdownOptions: {
      type: Object,
      default: () => ({
        categories: [],
        subcategories: [],
        titles: []
      })
    }
  },
  data() {
    return {
      form: {
        category: "",
        sub_category: "",
        title: "",
        description: "",
      },
      createRiskMitigationLoading: false,
      hasDescriptionError: false,
      localDropdownOptions: {
        categories: [],
        subcategories: [],
        titles: []
      },
    };
  },
  computed: {
    getDisableState() {
      return (
        !this.form.category ||
        !this.form.sub_category ||
        !this.form.title ||
        this.hasDescriptionError ||
        this.createRiskMitigationLoading
      );
    },
    getRiskAssessmentId() {
      return this.riskAssessmentId || this.$route.params.id;
    },
    multiSelectProps() {
      return {
        type: "multi-select",
        multiple: false,
        elementClass: (context, classes) => ['flex-1 min-w-full'].concat(classes),
        searchable: true,
        taggable: true,
        variant: "alt"
      };
    },
    isEditMode() {
      return this.mode === "edit";
    }
  },
  mounted() {
    this.localDropdownOptions = {
      categories: [...this.dropdownOptions.categories],
      subcategories: [...this.dropdownOptions.subcategories],
      titles: [...this.dropdownOptions.titles]
    };
  },
  watch: {
    formData: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.setFormData(newVal);
      }
    },
  },
  methods: {
    setFormData(data) {
      if (data && this.isEditMode) {
        let formData = {
          category: data.category,
          sub_category: data.sub_category,
          title: data.title,
          description: data.description,
        };
        this.form = formData;
      }
    },
    handleCancel() {
      this.form = {
        category: "",
        sub_category: "",
        title: "",
        description: "",
      };
      this.$emit("close");
    },
    async handleSubmit() {
      try {
        this.createRiskMitigationLoading = true;
        let payload = {
          category: this.form.category?.toString().trim(),
          sub_category: this.form.sub_category?.toString().trim(),
          title: this.form.title?.toString().trim(),
          description: this.form.description?.toString().trim(),
        };
        

        if (!this.isEditMode) {
          await createRiskMitigation({
            riskAssessmentId: this.riskAssessmentId, 
            payload
          });
        } else {
          await updateRiskMitigation({
            riskAssessmentId: this.riskAssessmentId, 
            mitigationId: this.formData.id,
            payload
          });
        }
        
        let successMessage = !this.isEditMode ? 
          this.$t('components.risk_mitigation.saved_successfully') : 
          this.$t('components.risk_mitigation.saved_successfully');
        this.$toast.success(successMessage);
        this.$emit("submitForm", payload);
        this.$emit("fetchUpdatedList");
        this.handleCancel();
        
        this.createRiskMitigationLoading = false;
      } catch (err) {
        console.error("err", err);
        this.$toast.error(err.response?.data?.detail || this.$t("general.default_error"));
        this.createRiskMitigationLoading = false;
      }
    },

    validationDescription(evt) {
      this.hasDescriptionError = evt.hasErrors;
    },
    addNewTag(newTag, fieldName, key) {
      if (!this.localDropdownOptions[fieldName].includes(newTag)) {
        this.localDropdownOptions[fieldName].push(newTag);
      }
      this.form[key] = newTag;
    },
  },
};
</script>